import React, { useState, useEffect } from 'react'
import { Modal, Button } from 'react-bootstrap'
import managerSettingsFns from '../../../../utils/helpers/managerSettingsFns'

const groupIsValid = (group) => group.name.trim().length > 0 && group.dockIds.length > 0

const SettingsModal = ({ show, onHide, settings, updateSettings, docks, locationId }) => {
    const [draft, setDraft] = useState(settings)
    const [copied, setCopied] = useState(false)
    const [importText, setImportText] = useState('')
    const [importError, setImportError] = useState('')
    // Only show group validation errors after the user has attempted to save
    const [saveAttempted, setSaveAttempted] = useState(false)
    const [confirmingDelete, setConfirmingDelete] = useState(false)
    const [showAdvanced, setShowAdvanced] = useState(false)

    // Sync draft when modal opens
    useEffect(() => {
        if (show) {
            setDraft(settings)
            setImportText('')
            setImportError('')
            setSaveAttempted(false)
            setConfirmingDelete(false)
            setShowAdvanced(false)
        }
    }, [show, settings])

    const handleDeleteSettings = () => {
        updateSettings(managerSettingsFns.getDefaultSettings())
        onHide()
    }

    const handleSave = () => {
        const invalidGroups = draft.dockGroups.filter(g => !groupIsValid(g))
        if (invalidGroups.length > 0) {
            setSaveAttempted(true)
            return
        }
        updateSettings(draft)
        onHide()
    }

    const handleAddGroup = () => {
        const newGroup = {
            id: Date.now().toString(),
            name: '',
            dockIds: []
        }
        setDraft({ ...draft, dockGroups: [...draft.dockGroups, newGroup] })
    }

    const handleRemoveGroup = (idx) => {
        setDraft({ ...draft, dockGroups: draft.dockGroups.filter((_, i) => i !== idx) })
    }

    const handleGroupNameChange = (idx, name) => {
        const updated = draft.dockGroups.map((g, i) => i === idx ? { ...g, name } : g)
        setDraft({ ...draft, dockGroups: updated })
    }

    const handleDockToggle = (idx, dockId, checked) => {
        const updatedIds = checked
            ? [...draft.dockGroups[idx].dockIds, dockId]
            : draft.dockGroups[idx].dockIds.filter(id => id !== dockId)
        const updated = draft.dockGroups.map((g, i) => i === idx ? { ...g, dockIds: updatedIds } : g)
        setDraft({ ...draft, dockGroups: updated })
    }

    const handleExport = () => {
        const exportData = { ...draft, locationId }
        const json = JSON.stringify(exportData, null, 2)
        navigator.clipboard.writeText(json).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
        })
    }

    const handleImport = () => {
        setImportError('')
        try {
            const parsed = JSON.parse(importText)
            if (parsed.locationId !== undefined && parsed.locationId !== locationId) {
                setImportError(`This config was exported from a different location (ID: ${parsed.locationId}). Dock group IDs will not match. Export from this location's settings to get a valid starting point.`)
                return
            }
            if (typeof parsed.widescreenMode !== 'boolean') throw new Error('Missing or invalid widescreenMode')
            if (!Array.isArray(parsed.dockGroups)) throw new Error('Missing or invalid dockGroups')
            const validatedGroups = parsed.dockGroups.map(g => {
                if (!g.id || !Array.isArray(g.dockIds)) throw new Error('Invalid group structure')
                return { id: g.id, name: g.name || '', dockIds: g.dockIds.filter(id => typeof id === 'number') }
            })
            setDraft({
                ...draft,
                widescreenMode: parsed.widescreenMode,
                horizontalScroll: typeof parsed.horizontalScroll === 'boolean' ? parsed.horizontalScroll : draft.horizontalScroll,
                minDockColumnWidth: typeof parsed.minDockColumnWidth === 'number' ? parsed.minDockColumnWidth : draft.minDockColumnWidth,
                dockGroups: validatedGroups
            })
            setImportText('')
        } catch (e) {
            setImportError('Invalid JSON or unexpected format: ' + e.message)
        }
    }

    // Real docks only (exclude the "All Docks" sentinel)
    const realDocks = docks ? docks.filter(d => d.id !== 99999999999999) : []

    return (
        <Modal show={show} onHide={onHide} size="lg">
            <Modal.Header>
                <Modal.Title>Dock Scheduler Settings</Modal.Title>
                <button type="button" className="btn btn-sm btn-outline-secondary ml-auto" onClick={onHide} aria-label="Close">✕</button>
            </Modal.Header>
            <Modal.Body>

                {/* ── Display Options ── */}
                <h6 className="border-bottom pb-1 mb-3">Display Options</h6>

                <div className="form-check mb-2">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="widescreenMode"
                        checked={draft.widescreenMode}
                        onChange={(e) => setDraft({ ...draft, widescreenMode: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="widescreenMode">
                        <strong>Widescreen Mode</strong> Removes the width constraint so the page spans the full browser width
                    </label>
                </div>

                <div className="form-check mb-2">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="horizontalScroll"
                        checked={draft.horizontalScroll}
                        onChange={(e) => setDraft({ ...draft, horizontalScroll: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="horizontalScroll">
                        <strong>Horizontal Scroll</strong> Sets a minimum column width for each dock; a scrollbar appears when docks don't fit
                    </label>
                </div>

                {draft.horizontalScroll && (
                    <div className="d-flex align-items-center mb-3 ml-4" style={{ paddingLeft: '1.5rem' }}>
                        <label className="mr-2 mb-0 small text-muted" style={{ marginRight: '0.5rem', whiteSpace: 'nowrap' }}>
                            Min column width (px):
                        </label>
                        <div>
                            {[80, 120, 160, 200, 250, 300, 400, 500].map(w => (
                                <button
                                    key={w}
                                    type="button"
                                    className={`btn btn-sm mr-1 ${draft.minDockColumnWidth === w ? 'btn-primary' : 'btn-outline-secondary'}`}
                                    style={{ marginRight: '0.25rem', marginBottom: '0.25rem' }}
                                    onClick={() => setDraft({ ...draft, minDockColumnWidth: w })}
                                >
                                    {w}
                                </button>
                            ))}
                            <input
                                type="number"
                                className="form-control form-control-sm d-inline-block"
                                style={{ width: '70px', display: 'inline-block', verticalAlign: 'middle' }}
                                min={40}
                                max={500}
                                value={draft.minDockColumnWidth}
                                onChange={(e) => setDraft({ ...draft, minDockColumnWidth: Number(e.target.value) })}
                            />
                        </div>
                    </div>
                )}

                <div className="form-check mb-3">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        id="showDockTypeOnHeader"
                        checked={draft.showDockTypeOnHeader}
                        onChange={(e) => setDraft({ ...draft, showDockTypeOnHeader: e.target.checked })}
                    />
                    <label className="form-check-label" htmlFor="showDockTypeOnHeader">
                        <strong>Show Dock Type on Column Header</strong> Displays "Inbound Only" or "Outbound Only" under the dock name in calendar column headers IF the dock is strictly inbound or outbound.  
                    </label>
                </div>

                {/* ── Dock Groups ── */}
                <h6 className="border-bottom pb-1 mb-2 mt-4">Dock Groups</h6>
                <p className="text-muted small mb-3">
                    Create named groups of docks. Each group appears as a tab and shows only those docks in the scheduler.
                    Docks not in any group still appear as individual tabs.
                </p>

                {draft.dockGroups.map((group, idx) => {
                    const invalid = saveAttempted && !groupIsValid(group)
                    const noName = saveAttempted && !group.name.trim()
                    const noDocks = saveAttempted && group.dockIds.length === 0
                    return (
                        <div
                            key={group.id}
                            className={`border rounded p-2 mb-2 ${invalid ? 'border-danger' : ''}`}
                        >
                            <div className="d-flex align-items-center mb-1">
                                <input
                                    type="text"
                                    className={`form-control form-control-sm ${noName ? 'is-invalid' : ''}`}
                                    style={{ marginRight: '0.5rem' }}
                                    placeholder="Group name (e.g. Morning Docks)"
                                    value={group.name}
                                    onChange={(e) => handleGroupNameChange(idx, e.target.value)}
                                />
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    style={{ whiteSpace: 'nowrap' }}
                                    onClick={() => handleRemoveGroup(idx)}
                                >
                                    Remove
                                </button>
                            </div>
                            {noName && <div className="text-danger small mb-1">A group name is required.</div>}
                            <div className="d-flex flex-wrap" style={{ gap: '0.5rem' }}>
                                {realDocks.map(dock => (
                                    <div key={dock.id} className="form-check form-check-inline" style={{ margin: 0 }}>
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`group-${idx}-dock-${dock.id}`}
                                            checked={group.dockIds.includes(dock.id)}
                                            onChange={(e) => handleDockToggle(idx, dock.id, e.target.checked)}
                                        />
                                        <label className="form-check-label small" htmlFor={`group-${idx}-dock-${dock.id}`}>
                                            {dock.name}
                                        </label>
                                    </div>
                                ))}
                            </div>
                            {noDocks && <div className="text-danger small mt-1">Select at least one dock.</div>}
                        </div>
                    )
                })}

                <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={handleAddGroup}>
                    + Add Group
                </button>

                {/* ── Export / Import / Delete ── */}
                <h6
                    className="border-bottom pb-1 mb-0 mt-4 pointerElement d-flex align-items-center justify-content-between"
                    onClick={() => setShowAdvanced(v => !v)}
                >
                    Export / Import / Delete Settings
                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>{showAdvanced ? '▲ hide' : '▼ show'}</span>
                </h6>

                {showAdvanced && (
                    <div className="mt-3">
                        <p className="text-muted small mb-2">
                            Export your settings as JSON to back them up or copy them to another device. Settings are saved per location.
                        </p>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary mb-3"
                            onClick={handleExport}
                        >
                            {copied ? 'Copied!' : 'Copy Settings to Clipboard'}
                        </button>

                        <div className="mb-3">
                            <label className="small font-weight-bold mb-1">Import Settings</label>
                            <textarea
                                className="form-control form-control-sm mb-1"
                                rows={4}
                                placeholder="Paste exported JSON here"
                                value={importText}
                                onChange={(e) => { setImportText(e.target.value); setImportError('') }}
                            />
                            {importError && <div className="text-danger small mb-1">{importError}</div>}
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={handleImport}
                                disabled={!importText.trim()}
                            >
                                Apply Import
                            </button>
                            <p className="text-muted small mt-1">
                                Importing updates the draft. Click <strong>Save Changes</strong> to apply.
                                Groups referencing docks not present at this location will appear empty.
                            </p>
                        </div>

                        <hr />

                        {!confirmingDelete ? (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => setConfirmingDelete(true)}
                            >
                                Delete My Settings
                            </button>
                        ) : (
                            <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                <span className="text-danger small">Delete all settings for this location?</span>
                                <button type="button" className="btn btn-sm btn-danger" onClick={handleDeleteSettings}>Yes, Delete</button>
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmingDelete(false)}>Cancel</button>
                            </div>
                        )}
                    </div>
                )}

            </Modal.Body>
            <Modal.Footer>
                {saveAttempted && draft.dockGroups.some(g => !groupIsValid(g)) && (
                    <span className="text-danger small mr-auto">
                        Each group needs a name and at least one dock selected.
                    </span>
                )}
                <Button variant="secondary" onClick={onHide}>Cancel</Button>
                <Button variant="primary" onClick={handleSave}>Save Changes</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default SettingsModal
